import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { FileSpreadsheet, AlertTriangle, Download, Upload, Database } from 'lucide-react';
import { toast } from 'sonner';
import { arrayMove } from '@dnd-kit/sortable';

// Componentes refactorizados
import { DeleteConfirmDialog, Pagination } from '../components/shared';
import { DestinationsTab, ClientsTab, EnsayosTab, EstadosTab, UsersTab } from '../components/config';
import BackupTab from '../components/config/BackupTab';

const Configuracion = () => {
  const { user } = useAuth();
  const [destinations, setDestinations] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ensayos, setEnsayos] = useState([]);
  const [estados, setEstados] = useState([]);
  const [users, setUsers] = useState([]);

  const [newDestination, setNewDestination] = useState('');
  const [newCliente, setNewCliente] = useState({ name: '', email: '', destinations: [] });
  const [editingCliente, setEditingCliente] = useState(null);
  const [newEstado, setNewEstado] = useState({ name: '', color: '#3b82f6', destinations: [] });
  const [editingEstado, setEditingEstado] = useState(null);
  const [newEnsayo, setNewEnsayo] = useState({ name: '', subcategories: [''] });
  const [editingEnsayo, setEditingEnsayo] = useState(null);

  // Import Excel state
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Pagination state for clients
  const [clientesPage, setClientesPage] = useState(1);
  const clientesPerPage = 15;

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: '', // 'destino', 'cliente', 'ensayo', 'estado', 'usuario'
    id: null,
    name: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Duplicate client dialog state
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    clientName: '',
    isFromExcel: false,
    duplicates: [], // For Excel import: list of duplicate names
  });

  // Import results dialog state
  const [importResultsDialog, setImportResultsDialog] = useState({
    open: false,
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [],
  });

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    is_superuser: false,
    assigned_destinations: [],
  });

  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    if (user?.is_superuser) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [destRes, clientesRes, ensayosRes, estadosRes, usersRes] = await Promise.all([
        axios.get('/destinations'),
        axios.get('/clientes'),
        axios.get('/ensayos'),
        axios.get('/estados'),
        axios.get('/users'),
      ]);

      setDestinations(destRes.data);
      // Ordenar clientes alfabéticamente por nombre
      const sortedClientes = [...clientesRes.data].sort((a, b) => 
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
      );
      setClientes(sortedClientes);
      // Ordenar ensayos alfabéticamente por nombre
      const sortedEnsayos = [...ensayosRes.data].sort((a, b) => 
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
      );
      setEnsayos(sortedEnsayos);
      setEstados(estadosRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Error al cargar la configuración');
      console.error(error);
    }
  };

  // Destinations
  const handleAddDestination = async () => {
    if (!newDestination.trim()) return;

    try {
      await axios.post('/destinations', { name: newDestination });
      toast.success('Destino creado correctamente');
      setNewDestination('');
      fetchData();
    } catch (error) {
      toast.error('Error al crear el destino');
    }
  };

  const handleDeleteDestination = (id, name) => {
    setDeleteDialog({ open: true, type: 'destino', id, name });
  };

  // Clientes
  const checkClienteExists = (name) => {
    return clientes.some(c => c.name.toLowerCase().trim() === name.toLowerCase().trim());
  };

  const handleAddCliente = async () => {
    if (!newCliente.name.trim()) return;

    // Check if client already exists
    if (checkClienteExists(newCliente.name)) {
      setDuplicateDialog({
        open: true,
        clientName: newCliente.name.trim(),
        isFromExcel: false,
        duplicates: [],
      });
      return;
    }

    await createCliente(newCliente.name, newCliente.destinations, newCliente.email);
  };

  const createCliente = async (name, destinations, email) => {
    try {
      await axios.post('/clientes', {
        name: name,
        email: email || null,
        destinations: destinations
      });
      toast.success('Cliente creado correctamente');
      setNewCliente({ name: '', email: '', destinations: [] });
      setClientesPage(1);
      fetchData();
    } catch (error) {
      toast.error('Error al crear el cliente');
    }
  };

  const handleEditCliente = (cliente) => {
    setEditingCliente({
      ...cliente,
      email: cliente.email || '',
      destinations: cliente.destinations || []
    });
  };

  const handleUpdateCliente = async () => {
    try {
      await axios.put(`/clientes/${editingCliente.id}`, {
        name: editingCliente.name,
        email: editingCliente.email || null,
        destinations: editingCliente.destinations
      });
      toast.success('Cliente actualizado correctamente');
      setEditingCliente(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el cliente');
    }
  };

  const toggleClienteDestination = (destination) => {
    if (editingCliente) {
      const destinations = editingCliente.destinations.includes(destination)
        ? editingCliente.destinations.filter(d => d !== destination)
        : [...editingCliente.destinations, destination];
      setEditingCliente({ ...editingCliente, destinations });
    } else {
      const destinations = newCliente.destinations.includes(destination)
        ? newCliente.destinations.filter(d => d !== destination)
        : [...newCliente.destinations, destination];
      setNewCliente({ ...newCliente, destinations });
    }
  };

  const handleDeleteCliente = (id, name) => {
    setDeleteDialog({ open: true, type: 'cliente', id, name });
  };

  // Import Clientes from Excel
  const [pendingExcelFile, setPendingExcelFile] = useState(null);

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('El archivo debe ser un Excel (.xlsx o .xls)');
      return;
    }

    setIsImporting(true);
    
    try {
      // First, check for duplicates
      const checkFormData = new FormData();
      checkFormData.append('file', file);
      
      const checkResponse = await axios.post('/clientes/check-duplicates-excel', checkFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const { duplicates, new_clients } = checkResponse.data;
      
      if (duplicates.length > 0) {
        // Store file for later use and show dialog
        setPendingExcelFile(file);
        setDuplicateDialog({
          open: true,
          clientName: '',
          isFromExcel: true,
          duplicates: duplicates,
          newClients: new_clients,
        });
        setIsImporting(false);
        return;
      }
      
      // No duplicates, proceed with import
      await proceedWithExcelImport(file);
      
    } catch (error) {
      console.error('Error checking duplicates:', error);
      toast.error(error.response?.data?.detail || 'Error al verificar duplicados');
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const proceedWithExcelImport = async (file) => {
    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/clientes/import-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { created, updated, skipped, errors } = response.data;
      
      // Show results in dialog
      setImportResultsDialog({
        open: true,
        created,
        updated,
        skipped,
        errors: errors || [],
      });
      
      setClientesPage(1);
      fetchData();
    } catch (error) {
      console.error('Error importing Excel:', error);
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    } finally {
      setIsImporting(false);
      setPendingExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDuplicateDialogConfirm = async () => {
    if (duplicateDialog.isFromExcel && pendingExcelFile) {
      // User confirmed to proceed with Excel import despite duplicates
      setDuplicateDialog({ open: false, clientName: '', isFromExcel: false, duplicates: [] });
      await proceedWithExcelImport(pendingExcelFile);
    }
  };

  const handleDuplicateDialogCancel = () => {
    setDuplicateDialog({ open: false, clientName: '', isFromExcel: false, duplicates: [] });
    setPendingExcelFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Ensayos
  const handleAddEnsayo = async () => {
    if (!newEnsayo.name.trim()) return;

    const subcategories = newEnsayo.subcategories.filter((s) => s.trim() !== '');
    try {
      await axios.post('/ensayos', { name: newEnsayo.name, subcategories });
      toast.success('Ensayo creado correctamente');
      setNewEnsayo({ name: '', subcategories: [''] });
      fetchData();
    } catch (error) {
      toast.error('Error al crear el ensayo');
    }
  };

  const handleUpdateEnsayo = async () => {
    if (!editingEnsayo) return;

    const subcategories = editingEnsayo.subcategories.filter((s) => s.trim() !== '');
    try {
      await axios.put(`/ensayos/${editingEnsayo.id}`, {
        name: editingEnsayo.name,
        subcategories,
      });
      toast.success('Ensayo actualizado correctamente');
      setEditingEnsayo(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el ensayo');
    }
  };

  const handleDeleteEnsayo = (id, name) => {
    setDeleteDialog({ open: true, type: 'ensayo', id, name });
  };

  const addSubcategory = (isEditing = false) => {
    if (isEditing && editingEnsayo) {
      setEditingEnsayo({
        ...editingEnsayo,
        subcategories: [...editingEnsayo.subcategories, ''],
      });
    } else {
      setNewEnsayo({
        ...newEnsayo,
        subcategories: [...newEnsayo.subcategories, ''],
      });
    }
  };

  const removeSubcategory = (index, isEditing = false) => {
    if (isEditing && editingEnsayo) {
      const newSubs = editingEnsayo.subcategories.filter((_, i) => i !== index);
      setEditingEnsayo({ ...editingEnsayo, subcategories: newSubs });
    } else {
      const newSubs = newEnsayo.subcategories.filter((_, i) => i !== index);
      setNewEnsayo({ ...newEnsayo, subcategories: newSubs });
    }
  };

  const updateSubcategory = (index, value, isEditing = false) => {
    if (isEditing && editingEnsayo) {
      const newSubs = [...editingEnsayo.subcategories];
      newSubs[index] = value;
      setEditingEnsayo({ ...editingEnsayo, subcategories: newSubs });
    } else {
      const newSubs = [...newEnsayo.subcategories];
      newSubs[index] = value;
      setNewEnsayo({ ...newEnsayo, subcategories: newSubs });
    }
  };

  // Estados
  const handleAddEstado = async () => {
    if (!newEstado.name.trim()) return;

    try {
      await axios.post('/estados', {
        name: newEstado.name,
        color: newEstado.color,
        destinations: newEstado.destinations
      });
      toast.success('Estado creado correctamente');
      setNewEstado({ name: '', color: '#3b82f6', destinations: [] });
      fetchData();
    } catch (error) {
      toast.error('Error al crear el estado');
    }
  };

  const handleEditEstado = (estado) => {
    setEditingEstado({
      id: estado.id,
      name: estado.name,
      color: estado.color,
      destinations: estado.destinations || [],
    });
  };

  const handleUpdateEstado = async () => {
    if (!editingEstado.name.trim()) return;

    try {
      await axios.put(`/estados/${editingEstado.id}`, {
        name: editingEstado.name,
        color: editingEstado.color,
        destinations: editingEstado.destinations,
      });
      toast.success('Estado actualizado correctamente');
      setEditingEstado(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  const toggleEstadoDestination = (destination) => {
    if (editingEstado) {
      const destinations = editingEstado.destinations.includes(destination)
        ? editingEstado.destinations.filter(d => d !== destination)
        : [...editingEstado.destinations, destination];
      setEditingEstado({ ...editingEstado, destinations });
    } else {
      const destinations = newEstado.destinations.includes(destination)
        ? newEstado.destinations.filter(d => d !== destination)
        : [...newEstado.destinations, destination];
      setNewEstado({ ...newEstado, destinations });
    }
  };

  const handleDeleteEstado = (id, name) => {
    setDeleteDialog({ open: true, type: 'estado', id, name });
  };

  // Users
  const handleAddUser = async () => {
    try {
      await axios.post('/auth/register', newUser);
      toast.success('Usuario creado correctamente');
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        is_superuser: false,
        assigned_destinations: [],
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el usuario');
    }
  };

  const handleDestinationToggle = (destName) => {
    const newDests = newUser.assigned_destinations.includes(destName)
      ? newUser.assigned_destinations.filter((d) => d !== destName)
      : [...newUser.assigned_destinations, destName];
    setNewUser({ ...newUser, assigned_destinations: newDests });
  };

  const handleEditDestinationToggle = (destName) => {
    const newDests = editingUser.assigned_destinations.includes(destName)
      ? editingUser.assigned_destinations.filter((d) => d !== destName)
      : [...editingUser.assigned_destinations, destName];
    setEditingUser({ ...editingUser, assigned_destinations: newDests });
  };

  const handleEditUser = (user) => {
    setEditingUser({
      email: user.email,
      full_name: user.full_name,
      is_superuser: user.is_superuser,
      assigned_destinations: user.assigned_destinations || [],
      new_password: '',
    });
  };

  const handleUpdateUser = async () => {
    try {
      const updateData = {
        email: editingUser.email,
        full_name: editingUser.full_name,
        is_superuser: editingUser.is_superuser,
        assigned_destinations: editingUser.assigned_destinations,
      };
      if (editingUser.new_password) {
        updateData.new_password = editingUser.new_password;
      }

      await axios.put(`/users/${encodeURIComponent(editingUser.email)}`, updateData);
      toast.success(editingUser.new_password ? 'Usuario y contraseña actualizados' : 'Usuario actualizado correctamente');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el usuario');
    }
  };

  const handleDeleteUser = (email, name) => {
    setDeleteDialog({ open: true, type: 'usuario', id: email, name: name || email });
  };

  // Confirm delete function
  const confirmDelete = async () => {
    if (!deleteDialog.id) return;
    
    setIsDeleting(true);
    try {
      let endpoint = '';
      let successMsg = '';
      
      switch (deleteDialog.type) {
        case 'destino':
          endpoint = `/destinations/${deleteDialog.id}`;
          successMsg = 'Destino eliminado correctamente';
          break;
        case 'cliente':
          endpoint = `/clientes/${deleteDialog.id}`;
          successMsg = 'Cliente eliminado correctamente';
          break;
        case 'ensayo':
          endpoint = `/ensayos/${deleteDialog.id}`;
          successMsg = 'Ensayo eliminado correctamente';
          break;
        case 'estado':
          endpoint = `/estados/${deleteDialog.id}`;
          successMsg = 'Estado eliminado correctamente';
          break;
        case 'usuario':
          endpoint = `/users/${encodeURIComponent(deleteDialog.id)}`;
          successMsg = 'Usuario eliminado correctamente';
          break;
        default:
          return;
      }
      
      await axios.delete(endpoint);
      toast.success(successMsg);
      fetchData();
    } catch (error) {
      toast.error(`Error al eliminar: ${error.response?.data?.detail || 'Error desconocido'}`);
    } finally {
      setIsDeleting(false);
      setDeleteDialog({ open: false, type: '', id: null, name: '' });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = estados.findIndex((estado) => estado.id === active.id);
      const newIndex = estados.findIndex((estado) => estado.id === over.id);

      const newEstados = arrayMove(estados, oldIndex, newIndex);
      
      // Update local state immediately
      setEstados(newEstados);

      // Send reorder to backend
      try {
        const estadosOrder = newEstados.map((estado, index) => ({
          id: estado.id,
          order: index,
        }));

        await axios.put('/estados/reorder', { estados: estadosOrder });
        toast.success('Orden de estados actualizado');
      } catch (error) {
        toast.error('Error al actualizar el orden de estados');
        // Revert on error
        fetchData();
      }
    }
  };

  if (!user?.is_superuser) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">
              Solo los superusuarios pueden acceder a la configuración.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="configuracion-container">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configuración</h1>
        <p className="text-gray-600 mt-1">
          Gestiona destinos, ensayos, estado y usuarios
        </p>
      </div>

      <Tabs defaultValue="destinations" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="destinations" data-testid="tab-destinations">
            Destinos
          </TabsTrigger>
          <TabsTrigger value="clientes" data-testid="tab-clientes">
            Clientes
          </TabsTrigger>
          <TabsTrigger value="ensayos" data-testid="tab-ensayos">
            Ensayos
          </TabsTrigger>
          <TabsTrigger value="estados" data-testid="tab-estados">
            Estado
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">
            Backup
          </TabsTrigger>
        </TabsList>

        {/* Destinations Tab */}
        <TabsContent value="destinations">
          <DestinationsTab
            destinations={destinations}
            newDestination={newDestination}
            onNewDestinationChange={setNewDestination}
            onAddDestination={handleAddDestination}
            onDeleteDestination={handleDeleteDestination}
          />
        </TabsContent>

        {/* Clientes Tab */}
        <TabsContent value="clientes">
          <ClientsTab
            clientes={clientes}
            destinations={destinations}
            newCliente={newCliente}
            onNewClienteChange={setNewCliente}
            editingCliente={editingCliente}
            onEditingClienteChange={setEditingCliente}
            onAddCliente={handleAddCliente}
            onEditCliente={handleEditCliente}
            onUpdateCliente={handleUpdateCliente}
            onDeleteCliente={handleDeleteCliente}
            onCancelEdit={() => setEditingCliente(null)}
            onToggleDestination={toggleClienteDestination}
            fileInputRef={fileInputRef}
            onImportExcel={handleImportExcel}
            isImporting={isImporting}
            currentPage={clientesPage}
            itemsPerPage={clientesPerPage}
            totalPages={Math.ceil(clientes.length / clientesPerPage)}
            onPageChange={setClientesPage}
            paginationComponent={
              <Pagination
                currentPage={clientesPage}
                totalPages={Math.ceil(clientes.length / clientesPerPage)}
                totalItems={clientes.length}
                startIndex={(clientesPage - 1) * clientesPerPage + 1}
                endIndex={Math.min(clientesPage * clientesPerPage, clientes.length)}
                onPrevPage={() => setClientesPage(prev => Math.max(1, prev - 1))}
                onNextPage={() => setClientesPage(prev => Math.min(Math.ceil(clientes.length / clientesPerPage), prev + 1))}
                hasPrevPage={clientesPage > 1}
                hasNextPage={clientesPage < Math.ceil(clientes.length / clientesPerPage)}
              />
            }
          />
        </TabsContent>

        {/* Ensayos Tab */}
        <TabsContent value="ensayos">
          <EnsayosTab
            ensayos={ensayos}
            newEnsayo={newEnsayo}
            onNewEnsayoChange={setNewEnsayo}
            editingEnsayo={editingEnsayo}
            onEditingEnsayoChange={setEditingEnsayo}
            onAddEnsayo={handleAddEnsayo}
            onUpdateEnsayo={handleUpdateEnsayo}
            onDeleteEnsayo={handleDeleteEnsayo}
            onCancelEdit={() => setEditingEnsayo(null)}
            onAddSubcategory={addSubcategory}
            onUpdateSubcategory={updateSubcategory}
            onRemoveSubcategory={removeSubcategory}
          />
        </TabsContent>

        {/* Estado Tab */}
        <TabsContent value="estados">
          <EstadosTab
            estados={estados}
            newEstado={newEstado}
            onNewEstadoChange={setNewEstado}
            editingEstado={editingEstado}
            onEditingEstadoChange={setEditingEstado}
            onAddEstado={handleAddEstado}
            onEditEstado={handleEditEstado}
            onUpdateEstado={handleUpdateEstado}
            onDeleteEstado={handleDeleteEstado}
            onToggleDestination={toggleEstadoDestination}
            onDragEnd={handleDragEnd}
          />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <UsersTab
            users={users}
            destinations={destinations}
            newUser={newUser}
            onNewUserChange={setNewUser}
            editingUser={editingUser}
            onEditingUserChange={setEditingUser}
            onAddUser={handleAddUser}
            onEditUser={handleEditUser}
            onUpdateUser={handleUpdateUser}
            onDeleteUser={handleDeleteUser}
            onCancelEdit={() => setEditingUser(null)}
            onDestinationToggle={handleDestinationToggle}
            onEditDestinationToggle={handleEditDestinationToggle}
          />
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup">
          <BackupTab />
        </TabsContent>

      </Tabs>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ ...deleteDialog, open: false })}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title={`¿Eliminar ${deleteDialog.type}?`}
        description="Esta acción no se puede deshacer."
        itemName={deleteDialog.name}
      />

      {/* Duplicate Client Dialog */}
      <AlertDialog open={duplicateDialog.open} onOpenChange={(open) => !open && handleDuplicateDialogCancel()}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle size={20} />
              {duplicateDialog.isFromExcel ? 'Clientes duplicados encontrados' : 'Cliente ya existe'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {duplicateDialog.isFromExcel ? (
                  <>
                    <p>Se encontraron <strong>{duplicateDialog.duplicates?.length || 0}</strong> clientes que ya existen en la base de datos:</p>
                    <div className="max-h-40 overflow-y-auto bg-amber-50 p-3 rounded border border-amber-200">
                      <ul className="text-sm space-y-1">
                        {duplicateDialog.duplicates?.slice(0, 10).map((name, idx) => (
                          <li key={idx} className="text-amber-800">• {name}</li>
                        ))}
                        {duplicateDialog.duplicates?.length > 10 && (
                          <li className="text-amber-600 italic">... y {duplicateDialog.duplicates.length - 10} más</li>
                        )}
                      </ul>
                    </div>
                    <p className="text-sm">
                      Si continúas, se actualizarán los destinos de los clientes existentes y se crearán <strong>{duplicateDialog.newClients?.length || 0}</strong> nuevos clientes.
                    </p>
                  </>
                ) : (
                  <p>El cliente <strong>"{duplicateDialog.clientName}"</strong> ya existe en la base de datos.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDuplicateDialogCancel}>
              Cancelar
            </AlertDialogCancel>
            {duplicateDialog.isFromExcel && (
              <AlertDialogAction
                onClick={handleDuplicateDialogConfirm}
                className="bg-amber-500 hover:bg-amber-600"
              >
                Continuar de todos modos
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Results Dialog */}
      <AlertDialog open={importResultsDialog.open} onOpenChange={(open) => !open && setImportResultsDialog({ ...importResultsDialog, open: false })}>
        <AlertDialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {importResultsDialog.errors.length > 0 ? (
                <AlertTriangle className="text-amber-500" size={20} />
              ) : (
                <FileSpreadsheet className="text-green-500" size={20} />
              )}
              Resultados de la Importación
            </AlertDialogTitle>
          </AlertDialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{importResultsDialog.created}</p>
                <p className="text-xs text-green-700">Creados</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{importResultsDialog.updated}</p>
                <p className="text-xs text-blue-700">Actualizados</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-600">{importResultsDialog.skipped}</p>
                <p className="text-xs text-gray-700">Sin cambios</p>
              </div>
            </div>

            {/* Errors Section */}
            {importResultsDialog.errors.length > 0 && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Errores encontrados ({importResultsDialog.errors.length})
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const text = importResultsDialog.errors.join('\n');
                      navigator.clipboard.writeText(text);
                      toast.success('Errores copiados al portapapeles');
                    }}
                  >
                    Copiar errores
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto bg-amber-50 p-3 rounded border border-amber-200">
                  <ul className="text-sm space-y-1">
                    {importResultsDialog.errors.map((error, idx) => (
                      <li key={idx} className="text-amber-800 py-1 border-b border-amber-100 last:border-0">
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <AlertDialogFooter className="mt-4">
            <AlertDialogAction onClick={() => setImportResultsDialog({ ...importResultsDialog, open: false })}>
              Cerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Configuracion;
